import { nullableArray, nullableOptional } from '@shigo-ui/api-client';
import { DashboardTableStatusTone } from '@shigo-ui/dashboard-ui';
import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';
import { customTargetingFormSchema } from '../../targeting/advancedTargetingModel';
import type { PersistedTargeting } from '../../targeting/model';
import { SchainType } from '../../supply/lists/model';
import { demandPartnerSchema } from '../partners/model';
import {
  placementSchema,
  PlacementDeliveryType,
  PlacementStatus,
  PlacementType,
  type PlacementSortDirection,
  type PlacementSortProperty,
} from '../../supply/placements/model';

import { IncompleteSchainHandling } from './incompleteSchainHandling';
export { IncompleteSchainHandling } from './incompleteSchainHandling';

export enum IntegrationStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Archived = 'ARCHIVED',
}

export enum IntegrationType {
  OpenRtb = 'ORTB',
  Vast = 'VAST',
  PrebidServer = 'PBS',
  Custom = 'CUSTOM',
}

export enum IntegrationMediaType {
  Banner = 'BANNER',
  Video = 'VIDEO',
  Audio = 'AUDIO',
  Native = 'NATIVE',
}

export enum IntegrationSortProperty {
  Name = 'name',
  Status = 'status',
  DemandPartner = 'demandPartner.displayName',
  IntegrationType = 'integrationType',
  CreatedOn = 'createdOn',
}

export enum IntegrationSortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

export enum IntegrationEditorTab {
  General = 'GENERAL',
  Integration = 'INTEGRATION',
  Rewrite = 'REWRITE',
  Targeting = 'TARGETING',
  Schain = 'SCHAIN',
  Pmp = 'PMP',
}

export enum IntegrationDetailsTab {
  Overview = 'overview',
  Journey = 'journey',
  Placements = 'placements',
}

export enum IntegrationSchainLogicMode {
  DirectOnly = 'DIRECT_ONLY',
  ResellerPriority = 'RESELLER_PRIORITY',
  DirectPriority = 'DIRECT_PRIORITY',
  ResellerOnly = 'RESELLER_ONLY',
}

export enum IntegrationMappingType {
  Bundle = 'BUNDLE',
  Domain = 'DOMAIN',
}

export enum IntegrationCustomParameterField {
  PublisherId = 'publisherId',
  SiteId = 'siteId',
  PlacementId = 'placementId',
  AppId = 'appId',
  ZoneId = 'zoneId',
  TagId = 'tagId',
  DisplayManager = 'displayManager',
  TripleliftInventoryCode = 'tripleliftInventoryCode',
  OpenxDelDomain = 'openxDelDomain',
  UnrulyTargetingUuid = 'unrulyTargetingUuid',
  OpenxUnitId = 'openxUnitId',
  SmartAdServerPageId = 'smartAdServerPageId',
  SmartAdServerNetworkId = 'smartAdServerNetworkId',
  SmartAdServerFormatId = 'smartAdServerFormatId',
  ImpExt = 'impExt',
  GroupId = 'groupId',
  BidRequestExtCo = 'bidRequestExtCo',
  BidRequestExt = 'bidRequestExt',
}

const integrationStatusSchema = z.preprocess(
  (value) =>
    Object.values(IntegrationStatus).includes(value as IntegrationStatus)
      ? value
      : IntegrationStatus.Inactive,
  z.enum(IntegrationStatus),
);

const integrationTypeSchema = z.preprocess(
  (value) =>
    Object.values(IntegrationType).includes(value as IntegrationType)
      ? value
      : IntegrationType.Custom,
  z.enum(IntegrationType),
);

export const integrationSchema = z.looseObject({
  id: z.coerce.string(),
  name: z.string(),
  status: integrationStatusSchema,
  demandPartner: demandPartnerSchema,
  integrationType: integrationTypeSchema,
  placementCnt: nullableOptional(z.number().int().nonnegative()),
  schainAsi: nullableOptional(z.string()),
  sellerId: nullableOptional(z.string()),
  duplicateDemandIntegration: nullableOptional(z.boolean()),
  notes: nullableOptional(z.string()),
  createdOn: nullableOptional(z.string()),
  updatedOn: nullableOptional(z.string()),
  mediaTypes: nullableOptional(z.string()),
  privateAuction: nullableOptional(z.boolean()),
  endpointUri: nullableOptional(z.string()),
  endpointUriApac: nullableOptional(z.string()),
  endpointUriEu: nullableOptional(z.string()),
  endpointUriUsEast: nullableOptional(z.string()),
  endpointUriUsWest: nullableOptional(z.string()),
  gzip: nullableOptional(z.boolean()),
  multipleImpressionsEnabled: nullableOptional(z.boolean()),
  multipleImpressionsCount: nullableOptional(z.number()),
  displayManager: nullableOptional(z.string()),
  advertiserId: nullableOptional(z.string()),
  targetCpm: nullableOptional(z.number()),
  bidFloor: nullableOptional(z.number()),
  qps: nullableOptional(z.number()),
  srpm7d: nullableOptional(z.number()),
  srpm7dChangePct: nullableOptional(z.number()),
  bidRatio7d: nullableOptional(z.number()),
  revenue7d: nullableOptional(z.number()),
  ecpm7d: nullableOptional(z.number()),
  shareOfVoice: nullableOptional(z.number()),
  biddingTmax: nullableOptional(z.number()),
  publisherId: nullableOptional(z.string()),
  siteId: nullableOptional(z.string()),
  appId: nullableOptional(z.string()),
  placementId: nullableOptional(z.string()),
  zoneId: nullableOptional(z.string()),
  tagId: nullableOptional(z.string()),
  tripleliftInventoryCode: nullableOptional(z.string()),
  openxDelDomain: nullableOptional(z.string()),
  unrulyTargetingUuid: nullableOptional(z.string()),
  openxUnitId: nullableOptional(z.string()),
  smartAdServerPageId: nullableOptional(z.string()),
  smartAdServerNetworkId: nullableOptional(z.string()),
  smartAdServerFormatId: nullableOptional(z.string()),
  impExt: nullableOptional(z.string()),
  groupId: nullableOptional(z.string()),
  bidRequestExtCo: nullableOptional(z.string()),
  bidRequestExt: nullableOptional(z.string()),
  blockBidsWithoutCat: nullableOptional(z.boolean()),
  blockBidsWithoutAdomain: nullableOptional(z.boolean()),
  dupCount: nullableOptional(z.number()),
  dupUseDefaultFloor: nullableOptional(z.boolean()),
  dupMinFloor: nullableOptional(z.number()),
  dupMaxFloor: nullableOptional(z.number()),
  dupIncrement: nullableOptional(z.number()),
  bundleMappingsCount: nullableOptional(z.number().int().nonnegative()),
  domainMappingsCount: nullableOptional(z.number().int().nonnegative()),
  integrationRewrite: nullableOptional(z.string()),
  listRewrites: nullableArray(
    z.looseObject({ id: z.coerce.string(), name: z.string().optional() }),
  ),
  listSchains: nullableArray(
    z.looseObject({
      id: z.coerce.string(),
      name: z.string().optional(),
      type: z.enum(SchainType).optional(),
    }),
  ),
  targeting: nullableOptional(z.looseObject({})),
  customTargeting: nullableOptional(customTargetingFormSchema),
  liverampTargeting: nullableOptional(z.unknown()),
  schainLogicMode: nullableOptional(z.enum(IntegrationSchainLogicMode)),
  incompleteSchainHandling: nullableOptional(z.enum(IncompleteSchainHandling)),
  deals: nullableArray(
    z.looseObject({
      id: nullableOptional(z.coerce.string()),
      initialId: nullableOptional(z.coerce.string()),
      dealId: nullableOptional(z.coerce.string()),
    }),
  ),
  demandCustomParams: nullableOptional(z.unknown()),
});

export const integrationsPageSchema = z.looseObject({
  content: z.array(integrationSchema),
  totalElements: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative().optional(),
  number: z.number().int().nonnegative().optional(),
  size: z.number().int().positive().optional(),
});

export const integrationStatusCountsSchema = z.preprocess(
  (value) => {
    if (Array.isArray(value)) {
      return Object.fromEntries(
        value
          .filter((item): item is Record<string, unknown> =>
            Boolean(item && typeof item === 'object'),
          )
          .map((item) => [String(item['status']), Number(item['count'] ?? 0)]),
      );
    }
    return value;
  },
  z
    .object({
      [IntegrationStatus.Active]: z.number().int().nonnegative().default(0),
      [IntegrationStatus.Inactive]: z.number().int().nonnegative().default(0),
      [IntegrationStatus.Archived]: z.number().int().nonnegative().default(0),
    })
    .partial(),
);

const integrationPlacementSchema = z
  .looseObject({
    id: z.union([z.string(), z.number()]).transform(String),
    name: z.string(),
    status: z
      .unknown()
      .transform((value) =>
        Object.values(PlacementStatus).includes(value as PlacementStatus)
          ? (value as PlacementStatus)
          : PlacementStatus.Inactive,
      ),
    type: z
      .unknown()
      .optional()
      .transform((value) =>
        Object.values(PlacementType).includes(value as PlacementType)
          ? (value as PlacementType)
          : PlacementType.Standard,
      ),
    deliveryType: z
      .unknown()
      .optional()
      .transform((value) =>
        Object.values(PlacementDeliveryType).includes(value as PlacementDeliveryType)
          ? (value as PlacementDeliveryType)
          : undefined,
      ),
  })
  .transform((placement) =>
    placementSchema.parse({
      ...placement,
      publishers: placement['publishers'] ?? [],
      publishersIds: placement['publishersIds'] ?? [],
      placementDemandIntegrations: placement['placementDemandIntegrations'] ?? [],
      targetingList: placement['targetingList'] ?? [],
      advertiserDomains: placement['advertiserDomains'] ?? [],
      excludedCreativeIds: placement['excludedCreativeIds'] ?? [],
      iabCategoriesExcluded: placement['iabCategoriesExcluded'] ?? [],
      impressionTrackers: placement['impressionTrackers'] ?? [],
      xpParametersMappings: placement['xpParametersMappings'] ?? [],
    }),
  );

export const integrationPlacementsPageSchema = z.looseObject({
  content: z.array(integrationPlacementSchema),
  totalElements: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative().optional(),
  number: z.number().int().nonnegative().optional(),
  size: z.number().int().positive().optional(),
});

export interface IntegrationsFilter {
  searchTerm: string;
  statuses: IntegrationStatus[];
  page: number;
  size: number;
  sortProperty: IntegrationSortProperty | null;
  sortDirection: IntegrationSortDirection;
}

export interface IntegrationPlacementsFilter {
  searchTerm: string;
  statuses: PlacementStatus[];
  types: PlacementType[];
  page: number;
  size: number;
  sortProperty: PlacementSortProperty | null;
  sortDirection: PlacementSortDirection;
}

export interface IntegrationMutationPayload {
  id?: string;
  name: string;
  status: IntegrationStatus;
  demandPartner: { id: string };
  integrationType: IntegrationType;
  mediaTypes: string;
  schainAsi: string;
  sellerId: string;
  notes: string;
  endpointUri: string;
  endpointUriApac: string;
  endpointUriEu: string;
  endpointUriUsEast: string;
  endpointUriUsWest: string;
  gzip: boolean;
  multipleImpressionsEnabled: boolean;
  multipleImpressionsCount: number | null;
  duplicateDemandIntegration: boolean;
  dupCount: number | null;
  dupUseDefaultFloor: boolean;
  dupMinFloor: number | null;
  dupMaxFloor: number | null;
  dupIncrement: number | null;
  displayManager: string;
  advertiserId: string;
  targetCpm: number | null;
  bidFloor: number | null;
  qps: number | null;
  shareOfVoice: number | null;
  biddingTmax: number | null;
  publisherId: string;
  siteId: string;
  appId: string;
  placementId: string;
  zoneId: string;
  tagId: string;
  tripleliftInventoryCode: string;
  openxDelDomain: string;
  unrulyTargetingUuid: string;
  openxUnitId: string;
  smartAdServerPageId: string;
  smartAdServerNetworkId: string;
  smartAdServerFormatId: string;
  impExt: string;
  groupId: string;
  bidRequestExtCo: string;
  bidRequestExt: string;
  blockBidsWithoutCat: boolean;
  blockBidsWithoutAdomain: boolean;
  integrationRewrite: string;
  targeting: PersistedTargeting;
  customTargeting: unknown;
  liverampTargeting: unknown;
  listRewrites: Array<{ id: string }>;
  listSchains: Array<{ id: string }>;
  schainLogicMode: IntegrationSchainLogicMode;
  incompleteSchainHandling: IncompleteSchainHandling;
  privateAuction: boolean;
  deals: Array<{ id: string }>;
  demandCustomParams?: Record<string, unknown>;
}

export type Integration = z.infer<typeof integrationSchema>;
export type IntegrationsPage = z.infer<typeof integrationsPageSchema>;
export type IntegrationPlacement = z.infer<typeof placementSchema>;

export const integrationPageSizes = [5, 10, 25, 50, 100] as const;

export const defaultIntegrationsFilter: IntegrationsFilter = {
  searchTerm: '',
  statuses: [IntegrationStatus.Active],
  page: 0,
  size: 10,
  sortProperty: IntegrationSortProperty.CreatedOn,
  sortDirection: IntegrationSortDirection.Desc,
};

export function isIntegrationSortProperty(value: string): value is IntegrationSortProperty {
  return Object.values(IntegrationSortProperty).includes(value as IntegrationSortProperty);
}

export function integrationStatusLabel(status: IntegrationStatus) {
  const labels = {
    [IntegrationStatus.Active]: dashboardT('integrations.status.active'),
    [IntegrationStatus.Inactive]: dashboardT('integrations.status.inactive'),
    [IntegrationStatus.Archived]: dashboardT('integrations.status.archived'),
  };
  return labels[status];
}

export function integrationMediaTypeLabel(mediaType: IntegrationMediaType) {
  const labels = {
    [IntegrationMediaType.Banner]: dashboardT('integrations.mediaType.banner'),
    [IntegrationMediaType.Video]: dashboardT('integrations.mediaType.video'),
    [IntegrationMediaType.Audio]: dashboardT('integrations.mediaType.audio'),
    [IntegrationMediaType.Native]: dashboardT('integrations.mediaType.native'),
  };
  return labels[mediaType];
}

export function integrationStatusTone(status: IntegrationStatus) {
  if (status === IntegrationStatus.Active) return DashboardTableStatusTone.Success;
  if (status === IntegrationStatus.Archived) return DashboardTableStatusTone.Neutral;
  return DashboardTableStatusTone.Warning;
}
